import React, { useState, useCallback, useEffect } from 'react';
import {
  Typography, Box, Button, Grid, Card, CardContent, CardMedia,
  IconButton, Snackbar, Alert, Paper, LinearProgress, Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { uploadMedia, listMedia, deleteMedia } from '../../utils/api';
import { MediaFile } from '../../utils/types';

const MediaManager: React.FC = () => {
  const [uploads, setUploads] = useState<MediaFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    const loadExisting = async () => {
      try {
        const files = await listMedia();
        setUploads(files);
      } catch (err) {
        console.error('Failed to load media', err);
      }
    };
    loadExisting();
  }, []);

  const handleUpload = async (files: FileList | null) => {
    if (!files) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const folder = file.type?.startsWith('video/') ? 'videos' : file.type?.startsWith('image/') ? 'images' : 'files';
        const media = await uploadMedia(file, folder);
        setUploads(prev => [media, ...prev]);
      }
      setSnackbar({ open: true, message: `Uploaded ${files.length} file(s)`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Upload failed', severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setSnackbar({ open: true, message: 'URL copied to clipboard', severity: 'success' });
  };

  const handleDelete = async (file: MediaFile) => {
    if (!window.confirm(`Delete "${file.name || file.key}"? This will also remove it from any articles that use it.`)) return;
    try {
      const result = await deleteMedia(file.key);
      setUploads(prev => prev.filter(f => f.key !== file.key));
      const msg = result.updatedArticles?.length
        ? `Deleted and removed from ${result.updatedArticles.length} article(s)`
        : 'Deleted';
      setSnackbar({ open: true, message: msg, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete file', severity: 'error' });
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleUpload(e.dataTransfer.files);
  }, []);

  const getIcon = (type?: string) => {
    if (type?.startsWith('video/')) return <VideoFileIcon sx={{ fontSize: 48 }} color="secondary" />;
    if (type?.startsWith('image/')) return <ImageIcon sx={{ fontSize: 48 }} color="primary" />;
    return <InsertDriveFileIcon sx={{ fontSize: 48 }} color="disabled" />;
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>Media</Typography>

      <Paper
        elevation={0}
        sx={{
          p: 6,
          textAlign: 'center',
          border: '2px dashed #CCC',
          borderRadius: 2,
          bgcolor: '#FAFAFA',
          cursor: 'pointer',
          mb: 4,
          '&:hover': { borderColor: 'primary.main', bgcolor: '#F5F0FF' },
        }}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.multiple = true;
          input.accept = 'image/*,video/*,.pdf,.doc,.docx';
          input.onchange = (e) => handleUpload((e.target as HTMLInputElement).files);
          input.click();
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="h6" color="text.secondary">
          Drop files here or click to upload
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Images, videos, PDFs, and documents
        </Typography>
      </Paper>

      {uploading && <LinearProgress sx={{ mb: 2 }} />}

      {uploads.length > 0 && (
        <>
          <Typography variant="h5" fontWeight={600} gutterBottom>Uploaded Files</Typography>
          <Grid container spacing={2}>
            {uploads.map((file) => (
              <Grid item xs={12} sm={6} md={4} key={file.key}>
                <Card elevation={0} sx={{ border: '1px solid #E0E0E0' }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {file.type?.startsWith('image/') ? (
                      <Box
                        component="img"
                        src={file.url}
                        sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1 }}
                      />
                    ) : (
                      getIcon(file.type)
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={600} noWrap>{file.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatSize(file.size)}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => copyUrl(file.url)} title="Copy URL">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(file)} title="Delete" color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MediaManager;
